import moment, { type Moment } from 'moment';
import type { IntlShape } from 'react-intl';

import type {
  FieldValue,
  FilterValues,
  SetValueProps,
} from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import {
  formatAbsolutePeriodForTag,
  formatRelativePeriodForTag,
  createPresetPeriodValue,
  isRelativePeriodValue,
  clampRangeToSpan,
  getMaxRelativeAmount,
  type PeriodDateType,
  type PeriodPresetId,
  type PeriodMaxSpan,
  type RelativeAbsolutePeriodValue,
} from '@src/components/RelativeAbsolutePeriodFilter';
import { normalizeCostCenterFilterIds } from '@src/packages/costCenters/utils/expandCostCenterIds';
import { translateString } from '@src/util/intl/translateString';
import { clampCalendarRangeToToday, DateUtil } from '@src/packages/utils/dates';

import type {
  ScheduleDashboardDateType,
  ScheduleDashboardFiltersStorage,
  SelectedSchedulesState,
} from '../SchedulesDashboard/types';

export const TASKS_DASHBOARD_FILTER_FIELDS = {
  RELATIVE: 'tasks.relative',
  DATE_RANGE: 'tasks.dateRange',
  GROUP_BY: 'tasks.groupBy',
  TEMPLATES: 'tasks.templates',
  COST_CENTERS: 'tasks.costCenterIds',
} as const;

export const TASKS_DASHBOARD_FILTERS_STORAGE_KEY = 'tasksDashboardFilters';

export const TASK_RELATIVE_DEFAULT = {
  isRelative: true,
  relativeAmount: 1,
  dateType: 'day' as ScheduleDashboardDateType,
};

export const TASK_GROUP_BY_DEFAULT: ScheduleDashboardDateType = 'day';

/**
 * Mirrors `MAX_HISTORY_RANGE_MONTHS` in the services `executionsHistory.schema.ts`: the backend
 * rejects wider searches, so the filters never offer (or restore) more than this.
 */
export const TASKS_REPORT_MAX_SPAN: PeriodMaxSpan = { amount: 3, unit: 'month' };

/** Absolute range offered by default: the last 30 days. */
export const DEFAULT_DATE_RANGE: [Moment, Moment] = [moment().subtract(30, 'day'), moment()];

export const DEFAULT_SELECTED_TEMPLATES: SelectedSchedulesState = {
  byId: {},
  selectedAll: true,
};

/** Explicit "nothing selected" state, kept distinct from the default "all". */
export const EMPTY_SELECTED_TEMPLATES: SelectedSchedulesState = {
  byId: {},
  selectedAll: false,
};

export interface TaskRelativeFilter {
  isRelative: boolean;
  relativeAmount: number;
  dateType: ScheduleDashboardDateType;
  periodMode?: 'preset' | 'absolute';
  periodPreset?: PeriodPresetId;
}

export interface TasksChartFilters {
  relative: TaskRelativeFilter;
  dateRange: [Moment, Moment];
  groupBy: ScheduleDashboardDateType;
  templates: SelectedSchedulesState;
  /** Selected cost-center ids (not expanded). Empty = no filter. */
  costCenterIds: string[];
}

export const buildDefaultTaskChartFilters = (): TasksChartFilters => {
  const period = createPresetPeriodValue(
    'currentMonth',
    undefined,
    DateUtil.getDefaultTimeZone(),
    TASKS_REPORT_MAX_SPAN,
    true,
  );

  return {
    relative: {
      isRelative: false,
      relativeAmount: 1,
      dateType: 'day',
      periodMode: 'preset',
      periodPreset: 'currentMonth',
    },
    dateRange: period.dateRange,
    groupBy: TASK_GROUP_BY_DEFAULT,
    templates: DEFAULT_SELECTED_TEMPLATES,
    costCenterIds: [],
  };
};

export const isTruthyRelativeMode = (relative: unknown): relative is TaskRelativeFilter =>
  relative != null &&
  typeof relative === 'object' &&
  !Array.isArray(relative) &&
  ((relative as TaskRelativeFilter).isRelative === true ||
    (relative as { isRelative?: unknown }).isRelative === 'true');

/** The view always opens with every template selected; an empty stored selection is not restored. */
const restoreStoredTemplates = (
  templates: SelectedSchedulesState | null | undefined,
): SelectedSchedulesState =>
  hasSelectedTemplates(templates) ? (templates as SelectedSchedulesState) : DEFAULT_SELECTED_TEMPLATES;

/**
 * Shrinks filters to the 3-month span instead of sending a search the backend rejects. Applied to
 * stored filters (predate the limit) and defensively to every outgoing query — the filter panel
 * is shared across dashboards, so a screen without this cap (e.g. Schedules) can leak a wider
 * `relative`/`dateRange` into the Tasks panel before it re-seeds its own values.
 */
export const limitToMaxSpan = (filters: TasksChartFilters): TasksChartFilters => {
  const { relative, dateRange } = filters;
  const dateType = relative.dateType as PeriodDateType;

  if (relative.isRelative) {
    const maxAmount = getMaxRelativeAmount(dateType, TASKS_REPORT_MAX_SPAN);

    if (maxAmount < 1) return { ...filters, relative: TASK_RELATIVE_DEFAULT };

    return {
      ...filters,
      relative: { ...relative, relativeAmount: Math.min(relative.relativeAmount, maxAmount) },
    };
  }

  const absoluteDateType = dateType === 'year' ? 'day' : dateType;
  return {
    ...filters,
    relative: { ...filters.relative, dateType: absoluteDateType },
    dateRange: clampRangeToSpan(clampCalendarRangeToToday(dateRange, DateUtil.getDefaultTimeZone()), absoluteDateType, TASKS_REPORT_MAX_SPAN),
  };
};

export const normalizeStoredTaskFilters = (
  raw: ScheduleDashboardFiltersStorage | TasksChartFilters | null | undefined,
): TasksChartFilters | null => {
  if (!raw || typeof raw !== 'object') return null;

  if ('relative' in raw && raw.relative && typeof raw.relative === 'object') {
    const relative = { ...TASK_RELATIVE_DEFAULT, ...raw.relative };
    let dateRange = (raw as TasksChartFilters).dateRange;
    if (Array.isArray(dateRange) && dateRange.length === 2) {
      dateRange = [moment(dateRange[0]), moment(dateRange[1])];
    } else {
      dateRange = [...DEFAULT_DATE_RANGE];
    }

    const costCenterIds = normalizeCostCenterFilterIds(
      (raw as TasksChartFilters).costCenterIds,
    );

    return limitToMaxSpan({
      relative,
      dateRange,
      groupBy: (raw as TasksChartFilters).groupBy ?? TASK_GROUP_BY_DEFAULT,
      templates: restoreStoredTemplates((raw as TasksChartFilters).templates),
      costCenterIds,
    });
  }

  const legacy = raw as ScheduleDashboardFiltersStorage;
  if (legacy.isRelative == null && legacy.dateType == null) return null;

  const start = legacy.dateRange?.[0];
  const end = legacy.dateRange?.[1];

  return limitToMaxSpan({
    relative: {
      isRelative: legacy.isRelative ?? TASK_RELATIVE_DEFAULT.isRelative,
      relativeAmount: legacy.relativeAmount ?? TASK_RELATIVE_DEFAULT.relativeAmount,
      dateType: legacy.dateType ?? TASK_RELATIVE_DEFAULT.dateType,
    },
    dateRange:
      start != null && end != null ? [moment(start), moment(end)] : [...DEFAULT_DATE_RANGE],
    groupBy: legacy.groupBy ?? TASK_GROUP_BY_DEFAULT,
    templates: DEFAULT_SELECTED_TEMPLATES,
    costCenterIds: [],
  });
};

export const filterValuesToTaskFilters = (
  values: Record<string, { value?: unknown }>,
): TasksChartFilters => {
  const relativeEntry = values[TASKS_DASHBOARD_FILTER_FIELDS.RELATIVE];
  const dateRangeEntry = values[TASKS_DASHBOARD_FILTER_FIELDS.DATE_RANGE];
  const groupByEntry = values[TASKS_DASHBOARD_FILTER_FIELDS.GROUP_BY];
  const templatesEntry = values[TASKS_DASHBOARD_FILTER_FIELDS.TEMPLATES];
  const costCentersEntry = values[TASKS_DASHBOARD_FILTER_FIELDS.COST_CENTERS];

  const rawRelative = relativeEntry?.value;
  let relative: TaskRelativeFilter = TASK_RELATIVE_DEFAULT;
  if (rawRelative === null) {
    relative = { ...TASK_RELATIVE_DEFAULT, isRelative: false };
  } else if (rawRelative && typeof rawRelative === 'object' && !Array.isArray(rawRelative)) {
    relative = { ...TASK_RELATIVE_DEFAULT, ...(rawRelative as TaskRelativeFilter) };
  }

  let dateRange: [Moment, Moment] = [...DEFAULT_DATE_RANGE];
  const rawDateRange = dateRangeEntry?.value;
  if (Array.isArray(rawDateRange) && rawDateRange.length === 2) {
    dateRange = [moment(rawDateRange[0]), moment(rawDateRange[1])];
  }

  const rawGroupBy = groupByEntry?.value;
  const groupBy =
    rawGroupBy != null && rawGroupBy !== ''
      ? (rawGroupBy as ScheduleDashboardDateType)
      : TASK_GROUP_BY_DEFAULT;

  const templates = parseTemplatesFilterValue(templatesEntry?.value);
  const costCenterIds = normalizeCostCenterFilterIds(costCentersEntry?.value);

  return limitToMaxSpan({
    relative: {
      ...relative,
      isRelative: isTruthyRelativeMode(relative),
    },
    dateRange,
    groupBy,
    templates,
    costCenterIds,
  });
};

export const filterValuesToTaskFiltersUi = (
  values: Record<string, { value?: unknown }>,
  fallback: TasksChartFilters,
): TasksChartFilters => {
  const hasTaskValues = Object.values(TASKS_DASHBOARD_FILTER_FIELDS).some(key =>
    Object.prototype.hasOwnProperty.call(values ?? {}, key),
  );
  if (!hasTaskValues) {
    return fallback;
  }

  return filterValuesToTaskFilters(values);
};

export const formatGroupByTag = (groupBy: ScheduleDashboardDateType | string, intl: IntlShape) => {
  if (groupBy == null || groupBy === '') return '';
  return intl.formatMessage({ id: `dateTypes.${groupBy}` }, { amount: 1 });
};

export const formatTemplatesForTag = (
  templates: SelectedSchedulesState | null | undefined,
  intl: IntlShape,
) => {
  if (!templates) return '';

  if (templates.selectedAll) {
    return intl.formatMessage({ id: 'general.all' });
  }

  const count = Object.keys(templates.byId).length;
  if (count === 0) return '';

  return intl.formatMessage({ id: 'dashboards.tasks.selectedCount' }, { count });
};

export const hasSelectedTemplates = (templates: SelectedSchedulesState | null | undefined) =>
  Boolean(templates && (templates.selectedAll || Object.keys(templates.byId).length > 0));

export const isEmptyTemplateSelection = (templates: SelectedSchedulesState | null | undefined) =>
  Boolean(templates) && !hasSelectedTemplates(templates);

/**
 * Filter value encoding: object = explicit selection, `[]` = nothing selected
 * (no template filter, no tag), `null` = filter cleared, back to all.
 */
const toTemplatesFilterValue = (templates: SelectedSchedulesState | null | undefined) => {
  if (!templates) return null;
  return isEmptyTemplateSelection(templates) ? [] : templates;
};

const parseTemplatesFilterValue = (raw: unknown): SelectedSchedulesState => {
  if (Array.isArray(raw)) return EMPTY_SELECTED_TEMPLATES;
  if (raw && typeof raw === 'object') return raw as SelectedSchedulesState;
  return DEFAULT_SELECTED_TEMPLATES;
};

export const mergeFilterSetValueProps = (
  current: FilterValues,
  props: SetValueProps,
): FilterValues => ({
  ...current,
  [props.key]: {
    ...current[props.key],
    value: props.value,
    ...(props.label !== undefined ? { label: props.label || '' } : {}),
    ...(props.valueTranslated !== undefined
      ? { valueTranslated: props.valueTranslated || '' }
      : {}),
    ...(props.valueType !== undefined ? { valueType: props.valueType } : {}),
    ...(props.clearValue !== undefined ? { clearValue: props.clearValue } : {}),
  },
});

export const getTemplateIdsForApi = (templates: SelectedSchedulesState): string[] =>
  templates.selectedAll ? [] : Object.keys(templates.byId).sort();

export const templateSelectionAffectsApiFetch = (
  current: SelectedSchedulesState,
  next: SelectedSchedulesState,
): boolean => getTemplateIdsForApi(current).join(',') !== getTemplateIdsForApi(next).join(',');

export const getTemplateSelectionKey = (templates: SelectedSchedulesState) => {
  if (templates.selectedAll) {
    return `all:${Object.keys(templates.byId).sort().join(',')}`;
  }

  return Object.keys(templates.byId).sort().join(',');
};

export const areTemplateSelectionsEqual = (
  current: SelectedSchedulesState,
  next: SelectedSchedulesState,
) => getTemplateSelectionKey(current) === getTemplateSelectionKey(next);

export const buildInitTaskFilterValues = (
  chartFilters: TasksChartFilters,
  intl: IntlShape,
): FilterValues => {
  const { relative, dateRange, groupBy, templates, costCenterIds } = chartFilters;
  const isRelative = isTruthyRelativeMode(relative);

  return {
    [TASKS_DASHBOARD_FILTER_FIELDS.RELATIVE]: {
      value: relative as unknown as FieldValue,
      label: translateString('dashboards.schedules.dateRange'),
      valueTranslated: isRelative ? formatRelativePeriodForTag(relative, intl) : '',
      valueType: isRelative ? ('string' as const) : ('null' as const),
      clearValue: null,
    },
    [TASKS_DASHBOARD_FILTER_FIELDS.DATE_RANGE]: {
      value: (isRelative ? null : dateRange) as unknown as FieldValue,
      label: translateString('dashboards.schedules.dateRange'),
      valueTranslated: isRelative ? '' : formatAbsolutePeriodForTag(dateRange),
      valueType: 'array' as const,
      clearValue: null,
    },
    [TASKS_DASHBOARD_FILTER_FIELDS.GROUP_BY]: {
      value: groupBy,
      label: translateString('dashboards.schedules.groupResultsBy'),
      valueTranslated: formatGroupByTag(groupBy, intl),
      valueType: 'string' as const,
      clearValue: null,
    },
    [TASKS_DASHBOARD_FILTER_FIELDS.TEMPLATES]: {
      value: toTemplatesFilterValue(templates) as unknown as FieldValue,
      label: translateString('schedules.type.task'),
      valueTranslated: formatTemplatesForTag(templates, intl),
      valueType: 'array' as const,
      clearValue: null,
    },
    [TASKS_DASHBOARD_FILTER_FIELDS.COST_CENTERS]: {
      value: (costCenterIds.length ? costCenterIds : null) as unknown as FieldValue,
      label: translateString('costCenters.filter.label'),
      valueTranslated: costCenterIds.length ? String(costCenterIds.length) : '',
      valueType: 'array' as const,
      clearValue: null,
    },
  };
};

export const taskFiltersToPeriodValue = (chart: TasksChartFilters): RelativeAbsolutePeriodValue => {
  const { relative, dateRange } = chart;
  const dateType = relative.dateType as PeriodDateType;

  if (relative.isRelative) {
    return { isRelative: true, relativeAmount: relative.relativeAmount, dateType };
  }

  return {
    isRelative: false,
    dateRange,
    dateType,
    mode: relative.periodMode ?? 'absolute',
    presetId: relative.periodPreset,
  };
};

export const periodValueToTaskFilterProps = (
  period: RelativeAbsolutePeriodValue,
  intl: IntlShape,
): SetValueProps[] => {
  const dateType = (period.dateType ?? TASK_RELATIVE_DEFAULT.dateType) as ScheduleDashboardDateType;

  if (isRelativePeriodValue(period)) {
    return [
      buildTaskFilterSetValueProps(
        TASKS_DASHBOARD_FILTER_FIELDS.RELATIVE,
        { isRelative: true, relativeAmount: period.relativeAmount, dateType },
        intl,
      ),
      buildTaskFilterSetValueProps(TASKS_DASHBOARD_FILTER_FIELDS.DATE_RANGE, null, intl),
    ];
  }

  return [
    {
      ...buildTaskFilterSetValueProps(TASKS_DASHBOARD_FILTER_FIELDS.RELATIVE, {
        isRelative: false,
        relativeAmount: 1,
        dateType,
        periodMode: period.mode ?? 'absolute',
        periodPreset: period.mode === 'preset' ? period.presetId ?? 'currentMonth' : undefined,
      }, intl),
      valueType: 'null',
      valueTranslated: '',
    },
    buildTaskFilterSetValueProps(
      TASKS_DASHBOARD_FILTER_FIELDS.DATE_RANGE,
      period.dateRange ?? [...DEFAULT_DATE_RANGE],
      intl,
    ),
  ];
};

export const taskFiltersToStoragePayload = (chart: TasksChartFilters) => ({
  relative: chart.relative,
  dateRange: chart.dateRange.map(date => moment(date).toISOString()),
  groupBy: chart.groupBy,
  templates: chart.templates,
  costCenterIds: chart.costCenterIds ?? [],
});

export const buildTaskFilterSetValueProps = (
  key: string,
  value: unknown,
  intl: IntlShape,
): SetValueProps => {
  switch (key) {
    case TASKS_DASHBOARD_FILTER_FIELDS.RELATIVE:
      return {
        key,
        value: value as SetValueProps['value'],
        label: translateString('dashboards.schedules.dateRange'),
        valueTranslated: formatRelativePeriodForTag(value, intl),
        valueType: 'string',
        clearValue: null,
      };
    case TASKS_DASHBOARD_FILTER_FIELDS.DATE_RANGE:
      return {
        key,
        value: value as SetValueProps['value'],
        label: translateString('dashboards.schedules.dateRange'),
        valueTranslated: formatAbsolutePeriodForTag(value as [Moment, Moment] | null),
        valueType: 'array',
        clearValue: null,
      };
    case TASKS_DASHBOARD_FILTER_FIELDS.GROUP_BY:
      return {
        key,
        value: value as SetValueProps['value'],
        label: translateString('dashboards.schedules.groupResultsBy'),
        valueTranslated: formatGroupByTag(value as ScheduleDashboardDateType, intl),
        valueType: 'string',
        clearValue: null,
      };
    case TASKS_DASHBOARD_FILTER_FIELDS.TEMPLATES: {
      const templates = value as SelectedSchedulesState | null;
      return {
        key,
        value: toTemplatesFilterValue(templates) as unknown as SetValueProps['value'],
        label: translateString('schedules.type.task'),
        valueTranslated: formatTemplatesForTag(templates, intl),
        valueType: 'array',
        clearValue: null,
      };
    }
    case TASKS_DASHBOARD_FILTER_FIELDS.COST_CENTERS: {
      const ids = Array.isArray(value)
        ? value.filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
        : [];
      return {
        key,
        value: (ids.length ? ids : null) as unknown as SetValueProps['value'],
        label: translateString('costCenters.filter.label'),
        valueTranslated: ids.length ? String(ids.length) : '',
        valueType: 'array',
        clearValue: null,
      };
    }
    default:
      return { key, value: value as SetValueProps['value'], valueType: 'string' };
  }
};
